/**
 * The screen frame: an area-coloured page, an optional photo masthead, and
 * the bottom navigation.
 *
 * `area` is not decoration. CLAUDE.md: colour is navigation, and each area
 * keeps its colour across every screen in that area — so a member two taps
 * deep in Programs still sees they are in Programs.
 *
 * `masthead` puts a photograph behind the title. Deliberately opt-in, and
 * deliberately absent from the ticket, the seat map and the door scanner:
 * those screens are working surfaces, and a photo there pushes the QR code or
 * the seats below the fold at exactly the moment someone needs them.
 */
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

export type Area =
  | "home"
  | "programs"
  | "bookings"
  | "membership"
  | "news"
  | "calendar"
  | "profile"
  | "about"
  | "danger";

/**
 * The default photo for each area, served from `public/photos/`. Swapping a
 * picture means replacing the file with the same name — no code change. An
 * area with no entry, or a file that is missing, falls back to the area
 * colour, which is what the masthead is painted with underneath the image.
 */
const PHOTO: Partial<Record<Area, string>> = {
  home: "home.jpg",
  programs: "programs.jpg",
  bookings: "bookings.jpg",
  profile: "profile.jpg",
};

/**
 * Where the subject sits in a photo, for the few where the centre is wrong.
 * A masthead is a wide short strip, so it shows a horizontal band — this
 * picks which band. The statue in home.jpg stands near the top of the frame.
 */
const FOCUS: Record<string, string> = {
  "home.jpg": "center 18%",
};

const TABS: { to: string; area: Area; key: string }[] = [
  { to: "/", area: "home", key: "nav.home" },
  { to: "/programs", area: "programs", key: "nav.programs" },
  { to: "/bookings", area: "bookings", key: "nav.bookings" },
  { to: "/profile", area: "profile", key: "nav.profile" },
];

export function Shell({
  area,
  title,
  children,
  back,
  masthead = false,
}: {
  area: Area;
  title?: string;
  children: ReactNode;
  back?: ReactNode;
  /** `true` uses the area's photo; a filename picks a specific one. */
  masthead?: boolean | string;
}) {
  const { t } = useTranslation();

  const photo = typeof masthead === "string" ? masthead : masthead ? PHOTO[area] : undefined;
  const headStyle = photo
    ? ({
        "--photo": `url(/photos/${photo})`,
        ...(FOCUS[photo] ? { "--photo-pos": FOCUS[photo] } : {}),
      } as CSSProperties)
    : undefined;

  return (
    <div data-area={area} className="screen">
      <header
        className={photo ? "screen__head screen__head--photo" : "screen__head"}
        style={headStyle}
      >
        {back}
        {title && <h1 className="screen__title">{title}</h1>}
      </header>
      <main className="screen__body">{children}</main>

      <nav className="tabs" aria-label={t("nav.home")}>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === "/"}
            data-area={tab.area}
            className={({ isActive }) => `tab${isActive ? " tab--active" : ""}`}
          >
            {/* Labels wrap rather than clip: `Acara & Pertunjukan` and
                `எனது பதிவுகள்` are both far longer than `My Bookings`. */}
            {t(tab.key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** A full-width message that is not an error — empty states, offline notes. */
export function Note({ children }: { children: ReactNode }) {
  return <p className="fallback-note">{children}</p>;
}

/** An error the member can act on. Always a translated sentence, never a
 *  server string, which would put English on an otherwise Tamil page. */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p data-area="danger" className="notice notice--error" role="alert">
      {children}
    </p>
  );
}
